/* eslint react/no-multi-comp: 0 */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  applySpec,
  complement,
  compose,
  concat,
  either,
  equals,
  filter,
  find,
  head,
  isEmpty,
  isNil,
  lensPath,
  map,
  of,
  pathOr,
  prop,
  propEq,
  path,
  pathSatisfies,
  reduce,
  replace,
  set,
  when,
} from 'ramda';
import { Query, withApollo } from 'react-apollo';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import Skeleton from 'react-loading-skeleton';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import debounce from 'debounce';
import { withSnackbar } from 'notistack';
import { SearchDropdown } from '@iris-platform/iris-react-ui';
import Autosuggest from 'react-autosuggest';
import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import { HasPermission } from '@iris-platform/protektor';
import { PROTEKTOR_DELETE, PROTEKTOR_UPDATE, PROTEKTOR_CREATE } from '../../utils/protektorConstants';
import {
  ASSOCIATE_USER_WITH_TEAM,
  ASSOCIATE_NEW_USER_WITH_TEAM,
  GET_USERS_FOR_TEAM,
  SEARCH_USERS,
  REMOVE_USER_FROM_TEAM,
  getAllRoles,
  GET_ROLE,
  GET_CURRENT_USER,
} from './queries';
import { handleChange, stylesToPropType, validations } from '../../utils';


const isNilOrEmpry = either(isNil, isEmpty);

const UsersPropType = PropTypes.arrayOf(
  PropTypes.shape({
    auminfo: PropTypes.shape({
      user_name: PropTypes.string,
      profile_url: PropTypes.string,
      avatar: PropTypes.string,
      email: PropTypes.string.isRequired,
      roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  }),
);

const ProvisionedUsersPropType = PropTypes.arrayOf(PropTypes.shape({
  domain: PropTypes.string.isRequired,
  profile: PropTypes.string,
  role: PropTypes.string.isRequired,
  user_email: PropTypes.string.isRequired,
}));

const roleSelectorStyles = () => ({
  root: {
    '& *': {
      width: 'auto',
      '& button': {
        width: '10.6em',
        height: '2.5em',
        backgroundImage: 'none',
        '&:not(:hover)': {
          backgroundColor: 'white',
        },
        '& *': {
          color: 'black',
        },
      },
    },
    '& [class^="MuiListItem"]': {
      cursor: 'pointer',
    },
  },
  disabledRole: {
    marginLeft: '1em',
    fontWeight: '500',
    fontSize: '15px',
    color: 'lightgray',
  },
});

const isRootRole = equals('root');

const RoleSelector = ({
  classes,
  value,
  onChange,
  disabled,
  selectedTeam,
}) => (
  <div className={classes.root}>
    <Query
      query={getAllRoles}
      fetchPolicy="network-only"
      notifyOnNetworkStatusChange
      variables={{ skip: 0, limit: 10 }}
    >
      {({ data, loading, error }) => {
        if (error) {
          return <h1>Oops, got error...</h1>;
        }
        if (loading) {
          return (
            <div>
              <h1>
                <Skeleton />
              </h1>
            </div>
          );
        }
        return (
          <div>
            {disabled
              ? (<p className={classes.disabledRole}>{value}</p>)
              : (
                <SearchDropdown
                  optionsList={data.allRoles.reduce((roles, item) => {
                    if (item.roleIdentifier.team === selectedTeam) {
                      roles.push({
                        code: item.roleIdentifier.name,
                        description: item.roleIdentifier.displayName,
                        team: item.roleIdentifier.team,
                      });
                    }
                    return roles;
                  }, [])
                      }
                  name="selected-role"
                  value={value}
                  placeholder="Select Role"
                  onSelect={option => onChange(option)}
                />
              )
    }
          </div>
        );
      }
    }
    </Query>
  </div>
);

RoleSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  classes: stylesToPropType(roleSelectorStyles()).isRequired,
};

RoleSelector.defaultProps = {
  value: '',
  disabled: false,
  onChange: () => {},
};

const StyledRoleSelector = withStyles(roleSelectorStyles)(RoleSelector);


const userRowsStyles = () => ({
  roleDropdown: {
    '& *': {
      width: 'auto',
    },
  },
  iconLeft: {
    marginRight: '0.25em',
  },
  addButton: {
    marginTop: '1em',
    width: '6em',
  },
  removeIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  iconDisabled: {
    fill: 'lightgray',
    '&:hover': {
      cursor: 'inherit',
    },
  },
  disabled: {
    color: 'lightgray',
    fontStyle: 'italic',
  },
});

const UserRows = ({
  classes,
  users,
  onRemove,
  onRoleChange,
  selectedTeam,
  permRole,
}) => (
  <Fragment>
    {map(({
      user_id: userId,
      auminfo: {
        user_name: userName,
        profile_url: profileUrl,
        roles,
        email,
      },
    }) => {
      const roleIdentifier = {};
      roleIdentifier.name = head(roles);
      return (
        <Query
          key={email}
          query={GET_ROLE}
          notifyOnNetworkStatusChange
          variables={{ roleIdentifier }}
        >
          {({ data, loading, error }) => (
            <Query
              query={GET_CURRENT_USER}
              notifyOnNetworkStatusChange
            >
              {({ data: userData, loading: userLoading, error: userError }) => {
                let displayName = '';
                if (error || userError) {
                  return 'Oops, got error...';
                }
                if (loading || userLoading) {
                  return (
                    <TableRow key="skeleton">
                      <TableCell>
                        <h1>
                          <Skeleton />
                        </h1>
                      </TableCell>
                      <TableCell>
                        <h1>
                          <Skeleton />
                        </h1>
                      </TableCell>
                      <TableCell>
                        <h1>
                          <Skeleton />
                        </h1>
                      </TableCell>
                      <TableCell>
                        <h1>
                          <Skeleton />
                        </h1>
                      </TableCell>
                    </TableRow>
                  );
                }
                if (data.role !== null) {
                  displayName = data.role.roleIdentifier.displayName;
                }
                return (
                  <TableRow key={email}>
                    <TableCell>
                      {profileUrl
                        ? (<a href={profileUrl}>{userName}</a>)
                        : userName}
                    </TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell className={classes.roleDropdown}>
                      <StyledRoleSelector
                        value={displayName || ''}
                        disabled={!permRole.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Teams' }) || userData.currentUser.user_id == userId}
                        onChange={event => onRoleChange(userId, event.code)}
                        selectedTeam={selectedTeam}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        id="tooltip-top-start"
                        title="Delete"
                        placement="top"
                      >
                        <div>
                          <IconButton
                            aria-label="Close"
                            disabled={loading || !permRole.hasPermission({ action: PROTEKTOR_DELETE, resource: 'Teams' })}
                            onClick={() => !isRootRole(head(roles)) && onRemove(userId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              }}
            </Query>
          )}
        </Query>
      );
    }, users)}
  </Fragment>
);

UserRows.propTypes = {
  users: UsersPropType.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  classes: stylesToPropType(userRowsStyles()).isRequired,
};

const StyledUserRows = withStyles(userRowsStyles)(UserRows);

const ProvisionedUserRows = ({
  classes,
  provisionedUsers,
  selectedTeam,
}) => (
  <Fragment>
    {map(({
      role = '',
      user_email: email,
    }) => {
      const roleIdentifier = {};
      roleIdentifier.name = replace(/^.*:/, '', role);
      return (
        <Query
          key={email}
          query={GET_ROLE}
          notifyOnNetworkStatusChange
          variables={{ roleIdentifier }}
        >
          {({ data, loading, error }) => {
            if (error) {
              return 'Oops, got error...';
            }
            if (loading) {
              return (
                <TableRow key="skeleton">
                  <TableCell>
                    <h1>
                      <Skeleton />
                    </h1>
                  </TableCell>
                  <TableCell>
                    <h1>
                      <Skeleton />
                    </h1>
                  </TableCell>
                  <TableCell>
                    <h1>
                      <Skeleton />
                    </h1>
                  </TableCell>
                  <TableCell>
                    <h1>
                      <Skeleton />
                    </h1>
                  </TableCell>
                </TableRow>
              );
            }

            return (
              <TableRow key={email}>
                <TableCell>
                  <p className={classes.disabled}>provisioned</p>
                </TableCell>
                <TableCell>{email}</TableCell>
                <TableCell className={classes.roleDropdown}>
                  <StyledRoleSelector
                    value={pathOr('', ['role', 'roleIdentifier', 'name'], data)}
                    disabled
                    selectedTeam={selectedTeam}
                  />
                </TableCell>
                <TableCell>
                  <RemoveIcon
                    onClick={() => {}}
                    className={`${classes.removeIcon} ${classes.iconDisabled}`}
                  />
                </TableCell>
              </TableRow>
            );
          }
  }
        </Query>
      );
    }, provisionedUsers)}
  </Fragment>
);

ProvisionedUserRows.propTypes = {
  provisionedUsers: ProvisionedUsersPropType.isRequired,
  classes: stylesToPropType(userRowsStyles()).isRequired,
};

const StyledProvisionedUserRows = withStyles(userRowsStyles)(ProvisionedUserRows);

const addUserRowStyles = () => ({
  container: {
    '& > td': {
      paddingTop: '1.5em',
      borderBottom: 'none',
    },
    '& .react-autosuggest__container': {
      position: 'relative',
    },
    '& .react-autosuggest__input': {
      width: '100%',
      height: '2em',
      padding: '10px 20px',
      fontWeight: '300',
      fontSize: 16,
      border: '1px solid #aaa',
      borderRadius: 4,
    },
    '& .react-autosuggest__input:focus': {
      outline: 'none',
    },
    '& .react-autosuggest__container--open .react-autosuggest__input': {
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0',
    },
    '& .react-autosuggest__suggestions-container': {
      display: 'none',
    },
    '& .react-autosuggest__container--open .react-autosuggest__suggestions-container': {
      display: 'block',
      position: 'absolute',
      top: 29,
      width: '100%',
      border: '1px solid #aaa',
      backgroundColor: '#fff',
      fontWeight: '300',
      fontSize: 16,
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      zIndex: '2',
    },
    '& .react-autosuggest__suggestions-list': {
      margin: '0',
      padding: '0',
      listStyleType: 'none',
    },
    '& .react-autosuggest__suggestion': {
      cursor: 'pointer',
      padding: '10px 20px',
    },
    '& .react-autosuggest__suggestion:not(:first-child)': {
      borderTop: '1px solid #ddd',
    },
    '& .react-autosuggest__suggestion--focused': {
      backgroundColor: '#0C7EAF',
      color: '#fff',
    },
  },
  cellSpacer: {
    height: '5em',
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  userSearchContainer: {
    paddingLeft: 0,
  },
  userSearch: {
    height: '5em',
    width: '100%',
  },
  userRole: {
    height: '5em',
  },
  iconLeft: {
    marginRight: '0.25em',
  },
  removeIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  addButton: {
    '& svg': {
      marginTop: '0.2em',
    },
  },
  suggestionContent: {
    display: 'flex',
    alignItems: 'center',
    backgroundRepeat: 'no-repeat',
  },
  highlight: {
    color: '#0272B6',
    fontWeight: 'bold',
  },
});

const getUserEmail = path(['auminfo', 'email']);

const getUserId = prop('user_id');

const convertUserToSuggestion = applySpec({
  id: getUserId,
  email: getUserEmail,
  description: getUserEmail,
});

class AddUserRow extends Component {
  state = {
    email: '',
    role: '',
    roleDisplay: '',
    suggestions: [],
    userSearchError: null,
    userRoleError: null,
    userIds: {},
  };

  updateSuggestions = debounce(async (searchString) => {
    const lowercaseSearchString = searchString.toLowerCase();
    const users = isEmpty(searchString)
      ? []
      : await this.fetchUsers(lowercaseSearchString);
    const nonDuplicateUsers = filter(complement(this.isDuplicateUser), users);
    const suggestions = this.convertUsersToSuggestions(nonDuplicateUsers);

    const newUserIds = reduce(
      (accum, user) => ({
        ...accum,
        [getUserEmail(user)]: getUserId(user),
      }),
      {},
      nonDuplicateUsers,
    );

    this.setState(state => ({
      suggestions,
      userIds: {
        ...state.userIds,
        ...newUserIds,
      },
    }));
  }, 300);

  convertUsersToSuggestions = compose(
    map(convertUserToSuggestion),
    filter(user => !this.isDuplicateEmail(getUserEmail(user))),
  );

  isDuplicateEmail = (email) => {
    const { users } = this.props;
    const duplicate = find(
      pathSatisfies(
        equals(email),
        ['auminfo', 'email'],
      ),
      users,
    );
    return !isNil(duplicate);
  };

  isDuplicateUser = (user) => {
    const email = getUserEmail(user);

    return this.isDuplicateEmail(email);
  };

  fetchUsers = async (searchString) => {
    const { client } = this.props;
    const { data: { users: { users } } } = await client.query({
      query: SEARCH_USERS,
      variables: {
        filter: searchString,
        skip: 0,
        limit: 20,
      },
    });

    return users;
  }

  addUser = async () => {
    const { onAdd, onAddNew } = this.props;
    const { email, role, userIds } = this.state;
    const lowercaseEmail = email.toLowerCase();
    const userId = userIds[lowercaseEmail];

    let userSearchError = null;
    let userRoleError = null;

    if (isNil(email) || isEmpty(email)) {
      userSearchError = new Error('Please provide an email');
    }

    if (this.isDuplicateEmail(email)) {
      userSearchError = new Error(`Can not add duplicate user ${email} to team`);
    }

    if (!validations.isEmail(email)) {
      userSearchError = new Error('Please provide a valid email');
    }

    if (isNil(role) || isEmpty(role)) {
      userRoleError = new Error('Please select a role for this user');
    }

    this.setState({
      userSearchError,
      userRoleError,
    });

    if (isNil(userSearchError) && isNil(userRoleError)) {
      if (userId) onAdd(userId, role);
      else onAddNew(email, role);

      this.setState({
        email: '',
        role: '',
        suggestions: [],
      });
    }
  }

  render() {
    const { classes, selectedTeam } = this.props;
    const {
      suggestions,
      email,
      userSearchError,
      userRoleError,
      userIds: knownUsers,
      roleDisplay,
    } = this.state;
    const lowercaseEmail = email.toLowerCase();
    const userSearchErrorMessage = prop('message', userSearchError);
    const hasUserSearchError = !(isNil(userSearchErrorMessage) || isEmpty(userSearchErrorMessage));
    const userRoleErrorMessage = prop('message', userRoleError);
    const hasUserRoleError = !(isNil(userRoleErrorMessage) || isEmpty(userRoleErrorMessage));
    const unknownEmail = (email && validations.isEmail(email) && !knownUsers[lowercaseEmail]);

    return (
      <TableRow className={classes.container}>
        <TableCell
          colSpan="2"
          className={classes.userSearchContainer}
        >
          <div className={classes.cellSpacer}>
            <FormControl
              className={classes.userSearch}
              error={hasUserSearchError}
            >
              <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={compose(this.updateSuggestions, prop('value'))}
                onSuggestionsClearRequested={() => this.updateSuggestions('')}
                getSuggestionValue={prop('email')}
                inputProps={{
                  placeholder: 'Email of the user to be added',
                  value: email,
                  onChange: (_, { newValue }) => this.setState({ email: newValue }),
                }}
                renderSuggestion={(suggestion, { query }) => {
                  const suggestionText = prop('email', suggestion);
                  const matches = AutosuggestHighlightMatch(suggestionText, query);
                  const parts = AutosuggestHighlightParse(suggestionText, matches);

                  return (
                    <span className="suggestion-content">
                      <span className="name">
                        {
                          parts.map(({ highlight, text }) => (
                            <span
                              className={highlight ? classes.highlight : ''}
                              key={text}
                            >
                              {text}
                            </span>
                          ))
                        }
                      </span>
                    </span>
                  );
                }}
              />
              {hasUserSearchError && (
                <FormHelperText>
                  {userSearchErrorMessage}
                </FormHelperText>
              )}
              {!hasUserSearchError && unknownEmail && (
                <FormHelperText error={false}>
                  {`${email} will be invited to join the 555 platform`}
                </FormHelperText>
              )}
            </FormControl>
          </div>
        </TableCell>
        <TableCell className={classes.userRoleContainer}>
          <div className={classes.cellSpacer}>
            <FormControl
              className={classes.userRole}
              error={hasUserRoleError}
            >
              <StyledRoleSelector
                value={roleDisplay}
                onChange={newRole => this.setState({ role: newRole.code, roleDisplay: newRole.description })}
                selectedTeam={selectedTeam}
              />
              {hasUserRoleError && <FormHelperText>{userRoleErrorMessage}</FormHelperText>}
            </FormControl>
          </div>
        </TableCell>
        <TableCell>
          <div className={`${classes.cellSpacer} ${classes.addButton}`}>
            <AddIcon
              onClick={this.addUser}
              className={classes.removeIcon}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  }
}

AddUserRow.propTypes = {
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  users: UsersPropType.isRequired,
  onAdd: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired,
  classes: stylesToPropType(addUserRowStyles()).isRequired,
};

const AddUserRowWithClient = withApollo(AddUserRow);

const StyledAddUserRow = withStyles(addUserRowStyles)(AddUserRowWithClient);

const teamEditorStyles = () => ({
  userTable: {
    '& td': {
      paddingLeft: '1em',
      paddingRight: '1em',
    },
  },
  iconLeft: {
    marginRight: '0.25em',
  },
  addButton: {
    marginTop: '1em',
    width: '6em',
  },
  removeIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  errorText: {
    color: '#f44336',
  },
  loading: {
    height: '0.8em !important',
    width: '0.8em !important',
    marginLeft: '0.8em',
  },
  title: {
    marginTop: '1em',
  },
});

class TeamEditor extends Component {
  constructor(props) {
    super(props);

    const { team, users, provisionedUsers } = props;

    this.state = {
      team,
      users,
      provisionedUsers,
      error: null,
      loading: false,
    };
  }

  mutate = async (mutation, createUpdate, description = 'update team') => {
    const { client, enqueueSnackbar } = this.props;

    this.setState({
      error: null,
      loading: true,
    });

    try {
      const { data } = await client.mutate(mutation);

      this.setState(state => ({
        loading: false,
        ...createUpdate(data, state),
      }));
      enqueueSnackbar('Successfully updated team', { variant: 'success' });
      return data;
    } catch (error) {
      this.setState({
        loading: false,
      });
      enqueueSnackbar('Unable to update role', { variant: 'error' });
      return {};
    }
  };

  addUser = (userId, role) => {
    const { team: { team_id: teamId } } = this.state;

    this.mutate(
      {
        mutation: ASSOCIATE_USER_WITH_TEAM,
        variables: { userId, teamId, role },
      },
      ({ associateUserWithTeam: newUser }, state) => ({
        users: concat(
          state.users,
          isNilOrEmpry(newUser) ? [] : of(newUser),
        ),
      }),
      'add user',
    );
  };

  addNewUser = (email, role) => {
    const { team: { team_id: teamId } } = this.state;
    this.mutate(
      {
        mutation: ASSOCIATE_NEW_USER_WITH_TEAM,
        variables: { email, teamId, role },
      },
      ({ provisionUserWithTeam: provisionedUser }, state) => ({
        provisionedUsers: concat(
          state.provisionedUsers,
          isNilOrEmpry(provisionedUser) ? [] : of(provisionedUser),
        ),
      }),
      'add new user',
    );
  };

  changeRole = (userId, role) => {
    const { enqueueSnackbar } = this.props;
    const { team: { team_id: teamId } } = this.state;
    this.mutate(
      {
        mutation: ASSOCIATE_USER_WITH_TEAM,
        variables: { userId, teamId, role },
      },
      ({ associateUserWithTeam: updatedUser }, state) => ({
        users: map(
          when(
            propEq('user_id', updatedUser.user_id),
            set(lensPath(['auminfo', 'roles']), updatedUser.auminfo.roles),
          ),
          state.users,
        ),
      }),
      'update role',
    );
  }

  removeUser = (userId) => {
    const { team: { team_id: teamId } } = this.state;

    this.mutate(
      {
        mutation: REMOVE_USER_FROM_TEAM,
        variables: { userId, teamId },
      },
      (_, state) => ({
        users: filter(
          complement(propEq('user_id', userId)),
          state.users,
        ),
      }),
      'remove user',
    );
  };

  render() {
    const { classes, role } = this.props;
    const {
      team,
      users,
      provisionedUsers,
      error,
      loading = false,
    } = this.state;

    const { display_name: teamDisplayName, team_id: teamID } = team;

    const hasError = !isNil(error) && !isNil(error.message);

    return (
      <Fragment>
        <Typography
          className={classes.title}
          component="h1"
          variant="title"
          gutterBottom
        >
          {teamDisplayName}
          {loading
          && (
          <CircularProgress classes={{
            root: classes.loading,
          }}
          />
          )}
        </Typography>
        {hasError && <FormHelperText className={classes.errorText}>{error.message}</FormHelperText>}
        <Table className={classes.userTable}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <StyledUserRows
              users={users}
              onRemove={this.removeUser}
              onRoleChange={this.changeRole}
              selectedTeam={teamID}
              permRole={role}
            />
            <StyledProvisionedUserRows
              provisionedUsers={provisionedUsers}
              selectedTeam={teamID}
            />
            <HasPermission
              to={PROTEKTOR_CREATE}
              access="Teams"
              forRole={role}
            >
              <StyledAddUserRow
                users={users}
                onAdd={this.addUser}
                onAddNew={this.addNewUser}
                selectedTeam={teamID}
              />
            </HasPermission>
          </TableBody>
        </Table>
      </Fragment>
    );
  }
}

TeamEditor.propTypes = {
  team: PropTypes.shape({
    team_id: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
  }).isRequired,
  users: UsersPropType.isRequired,
  provisionedUsers: ProvisionedUsersPropType.isRequired,
  classes: stylesToPropType(teamEditorStyles()).isRequired,
};

const TeamEditorWithClient = withApollo(TeamEditor);

const StyledTeamEditor = withStyles(teamEditorStyles)(TeamEditorWithClient);

const TeamEditorProvider = ({
  teamId,
  ...rest
}) => (
  <Query
    query={GET_USERS_FOR_TEAM}
    fetchPolicy="network-only"
    variables={{
      id: teamId,
      skip: 0,
      limit: 10,
    }}
    notifyOnNetworkStatusChange
  >
    {({ data, loading, error }) => {
      if (error) return 'ERROR';
      if (loading) {
        return (
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ borderBottom: 0 }} height={40}>
                  <h1>
                    <Skeleton />
                  </h1>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ borderBottom: 0 }} height={40}>
                  <Skeleton />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ borderBottom: 0 }} height={40}>
                  <Skeleton />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ borderBottom: 0 }} height={40}>
                  <Skeleton />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ borderBottom: 0 }} height={40}>
                  <Skeleton />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        );
      }
      return (
        <StyledTeamEditor
          team={pathOr([], ['usersForTeam', 'team'], data)}
          users={pathOr([], ['usersForTeam', 'users'], data)}
          provisionedUsers={pathOr([], ['getProvisionedUsers', 'users'], data)}
          {...rest}
        />
      );
    }}
  </Query>
);

TeamEditorProvider.propTypes = {
  teamId: PropTypes.string.isRequired,
};

export default withSnackbar(TeamEditorProvider);
